import { Avatar, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import BusinessIcon from "@mui/icons-material/Business";
import DropDown from "components/basic/autoComplete";
import EditIcon from "resources/images/EditIcon.png";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useEffect, useMemo, useRef, useState } from "react";
import Checkbox from "components/basic/checkbox";
import DoneIcon from "@mui/icons-material/CheckCircle";
import UploadFile from "components/basic/uploadFile";
import { useS3 } from "hooks/useS3";
import { useMaskedInput } from "hooks/useMaskedInput";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "components/basic/tooltip";
import logger from "utils/logger";
import GooglePlacesSearch, {
  PlaceResult,
} from "components/basic/googlePlacesSearch";

const EDIT_ICON_WIDTH = 20;

type MaskFn = (input: string, previous: string) => string;

const passThrough = (input: string) => input;

interface ListOptions {
  label: string;
  name: string;
  value?: string;
  isSelected?: boolean;
  mask?: MaskFn;
}

interface DropDownOption {
  selectedValue: string;
  optionList: {
    id: number;
    label: string;
    /** Stored value when `label` is translated; defaults to `label`. */
    value?: string;
  }[];
}

export type ProfileEditType =
  | "text"
  | "photo"
  | "logo"
  | "list"
  | "dropdown"
  | "address";

type ProfileEditInput = string | ListOptions[] | DropDownOption;

export interface ProfileEditButtonProps {
  label: string;
  input: ProfileEditInput;
  name: string;
  onChange?: (name: string, value: string | boolean) => void;
  /** Resolve `false` when the save was rejected, to put the old value back. */
  onSave?: (
    updates: Record<string, string | boolean | number>,
  ) => Promise<boolean | void>;
  type?: ProfileEditType;
  rows?: number;
  mask?: MaskFn;
  /** Help text under the label. */
  description?: string;
  /** Shown from an info icon beside the label. */
  info?: React.ReactNode;
  placeholder?: string;
}

function isListOptionsArray(input: any): input is ListOptions[] {
  return (
    Array.isArray(input) &&
    input.length > 0 &&
    "label" in input[0] &&
    "name" in input[0]
  );
}

function isDropDownOption(input: any): input is DropDownOption {
  return (
    typeof input === "object" &&
    input !== null &&
    "selectedValue" in input &&
    "optionList" in input &&
    Array.isArray(input.optionList)
  );
}

// Pure helper — derives the flat updates record from local state
function buildUpdates(
  type: ProfileEditType,
  name: string,
  localInput: ProfileEditInput,
  uploadedUrl?: string,
  place?: PlaceResult | null,
): Record<string, string | boolean | number> {
  if (type === "photo")
    return uploadedUrl ? { profilePhotoUrl: uploadedUrl } : {};
  if (type === "logo") return uploadedUrl ? { brandLogoURL: uploadedUrl } : {};
  if (type === "address" && typeof localInput === "string") {
    const updates: Record<string, string | number> = { [name]: localInput };
    // Only attach coordinates when the text still matches the picked place.
    if (place && place.address === localInput) {
      if (place.latitude != null) updates.latitude = place.latitude;
      if (place.longitude != null) updates.longitude = place.longitude;
      if (place.placeId) updates.placeId = place.placeId;
    }
    return updates;
  }
  if (isListOptionsArray(localInput)) {
    return Object.fromEntries(
      localInput
        .filter(
          (item) => item.isSelected !== undefined || item.value !== undefined,
        )
        .map((item) => [
          item.name,
          item.isSelected !== undefined ? item.isSelected : item.value!,
        ]),
    );
  }
  if (isDropDownOption(localInput)) return { [name]: localInput.selectedValue };
  if (typeof localInput === "string") return { [name]: localInput };
  return {};
}

export const ProfileEditButton: React.FC<ProfileEditButtonProps> = ({
  label,
  input,
  name,
  onChange = () => {},
  onSave,
  type = "text",
  rows = 1,
  mask,
  description,
  info,
  placeholder,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("profile");
  const { uploadToS3 } = useS3();

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Buffers edits locally — parent state is only updated on Done
  const [localInput, setLocalInput] = useState<ProfileEditInput>(input);

  // Sync localInput from the input prop whenever it changes outside of editing
  // (e.g. on initial load or if the parent updates it)
  useEffect(() => {
    if (!isEditing) {
      setLocalInput(input);
    }
    // Do not include isEditing in deps to avoid resetting localInput when toggling edit mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Only used for photo/logo types
  const [localFile, setLocalFile] = useState<File | null>(null);

  // Only used for the address type — holds the last picked place's coordinates
  const placeRef = useRef<PlaceResult | null>(null);

  // The value when editing began, restored if the save is rejected
  const committedInputRef = useRef<ProfileEditInput>(input);

  const startEditing = () => {
    logger.log(`ProfileEditButton: start editing "${name}"`);
    committedInputRef.current = input;
    setLocalInput(input); // snapshot current committed value
    setLocalFile(null);
    placeRef.current = null;
    setIsEditing(true);
  };

  // Called by sub-components during editing — writes to local state only
  const handleLocalChange = (fieldName: string, value: string | boolean) => {
    setLocalInput((prev) => {
      if (isListOptionsArray(prev)) {
        return prev.map((item) =>
          item.name === fieldName
            ? {
                ...item,
                ...(typeof value === "boolean"
                  ? { isSelected: value }
                  : { value: value as string }),
              }
            : item,
        );
      }
      if (isDropDownOption(prev)) {
        return { ...prev, selectedValue: value as string };
      }
      return value as string;
    });
  };

  // Commit local state to parent and save to backend
  const handleDone = async () => {
    setSaving(true);
    try {
      let uploadedUrl: string | undefined;
      if ((type === "photo" || type === "logo") && localFile) {
        const folder = type === "logo" ? "brand-logos" : "profile-photos";
        logger.log(`ProfileEditButton: uploading ${type} to S3/${folder}`);
        uploadedUrl = (await uploadToS3(localFile, folder)) ?? undefined;
        logger.log("ProfileEditButton: upload result", uploadedUrl);
      }

      const updates = buildUpdates(
        type,
        name,
        localInput,
        uploadedUrl,
        placeRef.current,
      );

      if (Object.keys(updates).length > 0) {
        logger.log(`ProfileEditButton: saving "${name}"`, updates);
        // Coordinates are numbers and only go to the backend, not parent state.
        Object.entries(updates).forEach(([k, v]) => {
          if (typeof v !== "number") onChange(k, v);
        });
        const saved = await onSave?.(updates);

        if (saved === false) {
          // Parent state was updated optimistically above; put it back.
          const committed = buildUpdates(type, name, committedInputRef.current);
          Object.entries(committed).forEach(([k, v]) => {
            if (k in updates && typeof v !== "number") onChange(k, v);
          });
          setLocalInput(committedInputRef.current);
        }
      } else {
        logger.log(`ProfileEditButton: nothing to save for "${name}"`);
      }

      setIsEditing(false);
    } catch (error) {
      logger.error(`ProfileEditButton: failed to save "${name}"`, error);
    } finally {
      setSaving(false);
    }
  };

  // Discard local edits and close
  const handleCancel = () => {
    logger.log(`ProfileEditButton: cancel editing "${name}"`);
    setLocalInput(input);
    setLocalFile(null);
    setIsEditing(false);
  };

  const renderField = () => {
    switch (type) {
      case "photo":
      case "logo":
        return typeof localInput === "string" ? (
          <ImageComponent
            variant={type}
            src={localInput}
            isEditing={isEditing}
            localFile={localFile}
            onFileChange={setLocalFile}
          />
        ) : null;
      case "list":
        return isListOptionsArray(localInput) ? (
          <ListComponent
            input={localInput}
            isEditing={isEditing}
            onChange={handleLocalChange}
          />
        ) : null;
      case "dropdown":
        return isDropDownOption(localInput) ? (
          <DropDownComponent
            input={localInput}
            name={name}
            isEditing={isEditing}
            onChange={handleLocalChange}
          />
        ) : null;
      case "address":
        return typeof localInput === "string" ? (
          <AddressComponent
            input={localInput}
            isEditing={isEditing}
            onInputChange={(text) => {
              placeRef.current = null;
              handleLocalChange(name, text);
            }}
            onPlaceSelect={(place) => {
              placeRef.current = place;
              handleLocalChange(name, place.address);
            }}
          />
        ) : null;
      default:
        return typeof localInput === "string" ? (
          <TextComponent
            name={name}
            input={localInput}
            isEditing={isEditing}
            rows={rows}
            mask={mask}
            placeholder={placeholder}
            onChange={handleLocalChange}
          />
        ) : null;
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(1),
        alignItems: "center",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}
      >
        <Button
          variant="text"
          // Button sets fontWeight inline and inline beats sx, so it stays here.
          style={{
            fontWeight: "bold",
            paddingLeft: EDIT_ICON_WIDTH,
            width: "fit-content",
          }}
          sx={{
            fontSize: { xs: "1rem", sm: 20 },
            // Long labels wrap on a phone, so the height can't be fixed or they
            // spill over the value below.
            whiteSpace: "normal",
            lineHeight: 1.3,
            paddingY: 0,
          }}
          onClick={isEditing ? handleDone : startEditing}
          disabled={saving}
          endAdornment={
            isEditing ? (
              <DoneIcon />
            ) : (
              <img
                src={EditIcon}
                alt={t("edit.edit")}
                width={EDIT_ICON_WIDTH}
                height={20}
              />
            )
          }
        >
          {saving ? t("edit.saving") : label}
        </Button>

        {isEditing && (
          <Button
            variant="text"
            style={{ height: "25px", minWidth: 0, padding: 0 }}
            onClick={handleCancel}
            disabled={saving}
            endAdornment={<CancelIcon />}
          >
            {""}
          </Button>
        )}

        {info && (
          <Tooltip title={info} arrow>
            <InfoIcon
              fontSize="small"
              tabIndex={0}
              aria-label={t("edit.about", { label })}
              sx={{ cursor: "help", color: theme.palette.text.secondary }}
            />
          </Tooltip>
        )}
      </div>

      {description && (
        <Typography
          variant="body2"
          align="center"
          style={{ opacity: 0.7, paddingBottom: theme.spacing(0.5) }}
        >
          {description}
        </Typography>
      )}

      {renderField()}
    </div>
  );
};

const ImageComponent: React.FC<{
  variant: "photo" | "logo";
  src: string;
  isEditing: boolean;
  localFile: File | null;
  onFileChange: (file: File | null) => void;
}> = ({ variant, src, isEditing, localFile, onFileChange }) => {
  const { t } = useTranslation("profile");
  // Object URLs must be revoked to avoid leaking the underlying blob
  const previewUrl = useMemo(
    () => (localFile ? URL.createObjectURL(localFile) : null),
    [localFile],
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (isEditing) {
    return (
      <UploadFile
        label={t(variant === "logo" ? "edit.upload_logo" : "edit.upload_photo")}
        onChange={(file) => onFileChange(file)}
        file={localFile}
      />
    );
  }
  logger.log("ImageComponent - src: ", src, ", previewURL: ", previewUrl);

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "center",
      }}
    >
      {variant === "logo" ? (
        <Avatar
          variant="square"
          alt={t("edit.logo_alt")}
          src={previewUrl ?? src}
          sx={{ width: "560px", height: 110 }}
        >
          <BusinessIcon />
        </Avatar>
      ) : (
        <Avatar
          alt={t("edit.photo_alt")}
          src={previewUrl ?? src}
          imgProps={{ referrerPolicy: "no-referrer" }}
          sx={{ width: 110, height: 110 }}
        />
      )}
    </div>
  );
};

const TextComponent: React.FC<{
  name: string;
  input: string;
  isEditing: boolean;
  rows: number;
  mask?: MaskFn;
  placeholder?: string;
  onChange?: (name: string, value: string) => void;
}> = ({
  name,
  input,
  isEditing,
  rows,
  mask,
  placeholder,
  onChange = () => {},
}) => {
  const handleMaskedChange = useMaskedInput(mask ?? passThrough);

  return (
    <div style={{ width: "100%" }}>
      {isEditing ? (
        <TextField
          variant="outlined"
          name={name}
          value={input}
          textAlign="center"
          style={{ width: "100%" }}
          rows={rows}
          placeHolder={placeholder}
          onChange={(event) =>
            onChange(
              event.target.name,
              mask ? handleMaskedChange(event, input) : event.target.value,
            )
          }
        />
      ) : (
        <Typography align="center">{input}</Typography>
      )}
    </div>
  );
};

const AddressComponent: React.FC<{
  input: string;
  isEditing: boolean;
  onInputChange: (text: string) => void;
  onPlaceSelect: (place: PlaceResult) => void;
}> = ({ input, isEditing, onInputChange, onPlaceSelect }) => {
  const { t } = useTranslation("profile");
  if (!isEditing) {
    return <Typography align="center">{input}</Typography>;
  }
  return (
    <div style={{ width: "100%" }}>
      <GooglePlacesSearch
        value={input}
        textAlign="center"
        placeHolder={t("edit.search_address")}
        onInputChange={onInputChange}
        onSelect={onPlaceSelect}
      />
    </div>
  );
};

const DropDownComponent: React.FC<{
  input: DropDownOption;
  name: string;
  isEditing: boolean;
  onChange?: (name: string, value: string | boolean) => void;
}> = ({ input, name, isEditing, onChange = () => {} }) => {
  return (
    <div style={{ width: "100%" }}>
      {isEditing ? (
        // Full width like the text fields, so long values such as emails fit.
        <DropDown
          value={
            input.optionList.find(
              (option) =>
                (option.value ?? option.label) === input.selectedValue,
            )?.id
          }
          options={input.optionList}
          onChange={(value) => {
            const picked = input.optionList.find(({ id }) => id === value?.id);
            onChange(name, picked ? (picked.value ?? picked.label) : "");
          }}
          textAlign="center"
        />
      ) : (
        <Typography align="center" style={{ overflowWrap: "anywhere" }}>
          {input.optionList.find(
            (option) => (option.value ?? option.label) === input.selectedValue,
          )?.label ?? input.selectedValue}
        </Typography>
      )}
    </div>
  );
};

const ListComponent: React.FC<{
  input: ListOptions[];
  isEditing: boolean;
  onChange?: (name: string, value: string | boolean) => void;
}> = ({ input, isEditing, onChange = () => {} }) => {
  return (
    <div style={{ width: "100%" }}>
      {input.map((item, index) => {
        if (isEditing) {
          if (item.isSelected !== undefined) {
            return (
              <ListWithCheckboxComponent
                key={item.name}
                item={item}
                onChange={onChange}
              />
            );
          }

          return (
            <ListWithTextBoxComponent
              key={item.name}
              item={item}
              onChange={onChange}
            />
          );
        }

        if (item.isSelected) {
          return (
            <Typography align="center" key={index}>
              {item.label}
            </Typography>
          );
        }

        if (item.isSelected === undefined) {
          return (
            <Typography align="center" key={index}>
              {item.value}
            </Typography>
          );
        }

        return null;
      })}
    </div>
  );
};

const ListWithCheckboxComponent: React.FC<{
  item: ListOptions;
  onChange?: (name: string, value: string | boolean) => void;
}> = ({ item, onChange = () => {} }) => {
  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <Checkbox
        isChecked={item.isSelected ?? false}
        onChange={(checked) => onChange(item.name, checked)}
        label={item.label}
        labelPlacement="start"
      />
    </div>
  );
};

const ListWithTextBoxComponent: React.FC<{
  item: ListOptions;
  onChange?: (name: string, value: string | boolean) => void;
}> = ({ item, onChange = () => {} }) => {
  const theme = useTheme();

  const handleMaskedChange = useMaskedInput(item.mask ?? passThrough);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        paddingTop: theme.spacing(1),
      }}
    >
      <TextField
        variant="outlined"
        label={item.label}
        name={item.name}
        value={item.value}
        textAlign="center"
        style={{ width: "100%" }}
        onChange={(event) => {
          onChange(
            event.target.name,
            item.mask
              ? handleMaskedChange(event, item.value ?? "")
              : event.target.value,
          );
        }}
      />
    </div>
  );
};
